import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { clinicianService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import SOSButton from '../components/SOSButton';
import { Card, Badge } from '../components/ui';

const phaseColors = {
  1: 'bg-blue-100 text-blue-800',
  2: 'bg-purple-100 text-purple-800',
  3: 'bg-green-100 text-green-800',
  4: 'bg-yellow-100 text-yellow-800'
};

const ClientProfileView = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [unassignLoading, setUnassignLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    identity: true,
    engagement: true,
    moodCraving: false,
    journal: false,
    homework: false,
    encouragement: false,
    consent: false
  });
  const [timeWindow, setTimeWindow] = useState('30'); // 7, 30, or 90 days
  const [homeworkFilter, setHomeworkFilter] = useState('all');

  useEffect(() => {
    loadClientProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const loadClientProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await clinicianService.getClientProfile(clientId);
      setProfile(response.data.data);
    } catch (err) {
      console.error('Failed to load client profile:', err);
      if (err.response?.status === 403) {
        setError(err.response.data.consentRequired 
          ? 'Client has not granted consent to data sharing'
          : 'Not authorized to view this client'
        );
      } else {
        setError('Failed to load client profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnassignSelf = async () => {
    if (!window.confirm('Remove yourself from this client? The client account will remain active but unassigned.')) {
      return;
    }

    setUnassignLoading(true);
    try {
      await clinicianService.unassignSelf(clientId);
      // Redirect back to dashboard after successful unassignment
      navigate('/clinician/dashboard');
    } catch (err) {
      console.error('Failed to unassign:', err);
      alert(err.response?.data?.message || 'Failed to remove yourself from this client.');
      setUnassignLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  const getDaysSince = (date) => {
    if (!date) return null;
    const days = Math.floor((Date.now() - new Date(date)) / (1000 * 60 * 60 * 24));
    return days;
  };

  const getEngagementStatus = (lastActive) => {
    const days = getDaysSince(lastActive);
    if (days === null || days > 6) return { label: 'Away', color: 'bg-gray-100 text-gray-800' };
    if (days <= 2) return { label: 'Consistent', color: 'bg-green-100 text-green-800' };
    return { label: 'Inconsistent', color: 'bg-yellow-100 text-yellow-800' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <LoadingSpinner />
        </main>
        <SOSButton />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <Navigation />
        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="p-6">
            <div className="text-center">
              <div className="text-red-500 text-lg font-semibold mb-4">
                {error}
              </div>
              <button
                onClick={() => navigate('/clinician/dashboard')}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Back to Dashboard
              </button>
            </div>
          </Card>
        </main>
        <SOSButton />
      </div>
    );
  }

  if (!profile) {
    return null;
  }

  const { identity, engagement, pillarBalance, milestones, moodCravingTrends, 
    journalEngagement, homework, encouragementHistory, 
    consentStatus } = profile;

  const filteredHomeworkAssignments = (homework?.assignments || []).filter((assignment) => {
    if (homeworkFilter === 'all') return true;
    if (homeworkFilter === 'active') return assignment.completionStatus === 'active' || assignment.completionStatus === 'overdue';
    return assignment.completionStatus === homeworkFilter;
  });

  const engagementStatus = getEngagementStatus(identity.lastActive);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <Navigation />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button and Actions */}
        <div className="mb-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/clinician/dashboard')}
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          <button
            onClick={handleUnassignSelf}
            disabled={unassignLoading}
            className="px-4 py-2 text-sm font-semibold rounded-lg border-2 border-warning-300 bg-white text-warning-700 hover:bg-warning-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {unassignLoading ? '👋 Removing...' : '👋 Remove Myself'}
          </button>
        </div>

        {/* Section 1: Client Identity & Program Status */}
        <Card className="mb-6">
          <div
            className="flex justify-between items-center p-6 cursor-pointer"
            onClick={() => toggleSection('identity')}
          >
            <h2 className="text-2xl font-bold text-gray-900">
              {identity.displayName}
            </h2>
            <svg
              className={`w-6 h-6 transform transition-transform ${expandedSections.identity ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.identity && (
            <div className="px-6 pb-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Current Progress</div>
                  <div className="text-2xl font-bold text-indigo-600">
                    Day {identity.currentDay} of {identity.totalDays}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600">Current Phase</div>
                  <div className="flex items-center gap-2">
                    <Badge className={phaseColors[identity.currentPhase]}>
                      Phase {identity.currentPhase}: {identity.phaseName}
                    </Badge>
                  </div>
                  <div className="mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-indigo-600 h-2 rounded-full"
                        style={{ width: `${identity.phaseProgressPercentage}%` }}
                      />
                    </div>
                    <div className="text-xs text-gray-600 mt-1">
                      {identity.phaseProgressPercentage}% through phase
                    </div>
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600">Engagement Status</div>
                  <Badge className={engagementStatus.color}>
                    {engagementStatus.label}
                  </Badge>
                  <div className="text-xs text-gray-600 mt-1">
                    Last active: {formatDateTime(identity.lastActive)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t">
                <div>
                  <div className="text-sm text-gray-600">Enrollment Date</div>
                  <div className="font-medium">{formatDate(identity.enrollmentDate)}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600">Pathway Type</div>
                  <div className="font-medium">{identity.pathwayType}</div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600">Account Status</div>
                  <Badge className={
                    identity.accountStatus === 'active' ? 'bg-green-100 text-green-800' :
                    identity.accountStatus === 'suspended' ? 'bg-red-100 text-red-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {identity.accountStatus}
                  </Badge>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Graduation Status</div>
                  <Badge className={
                    identity.graduationStatus === 'graduated' ? 'bg-purple-100 text-purple-800' :
                    identity.graduationStatus === 'eligible' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }>
                    {identity.graduationStatus.replace('_', ' ')}
                  </Badge>
                </div>

                <div>
                  <div className="text-sm text-gray-600">Faith Pillar</div>
                  <div className="font-medium">
                    {identity.faithEnabled ? 'Enabled (4-pillar)' : 'Disabled (3-pillar)'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Section 2: Engagement Metrics */}
        <Card className="mb-6">
          <div
            className="flex justify-between items-center p-6 cursor-pointer border-b"
            onClick={() => toggleSection('engagement')}
          >
            <h3 className="text-xl font-bold text-gray-900">Engagement Metrics</h3>
            <svg
              className={`w-6 h-6 transform transition-transform ${expandedSections.engagement ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.engagement && (
            <div className="p-6">
              <div className="flex gap-4 mb-6">
                <button
                  onClick={() => setTimeWindow('7')}
                  className={`px-4 py-2 rounded-lg ${timeWindow === '7' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  7 Days
                </button>
                <button
                  onClick={() => setTimeWindow('30')}
                  className={`px-4 py-2 rounded-lg ${timeWindow === '30' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  30 Days
                </button>
                <button
                  onClick={() => setTimeWindow('90')}
                  className={`px-4 py-2 rounded-lg ${timeWindow === '90' ? 'bg-indigo-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                >
                  90 Days
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Current Streak</div>
                  <div className="text-3xl font-bold text-blue-600">
                    {engagement.currentStreak}
                  </div>
                  <div className="text-xs text-gray-600">days</div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Longest Streak</div>
                  <div className="text-3xl font-bold text-purple-600">
                    {engagement.longestStreak}
                  </div>
                  <div className="text-xs text-gray-600">days</div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Total Days</div>
                  <div className="text-3xl font-bold text-green-600">
                    {engagement.totalDaysCompleted}
                  </div>
                  <div className="text-xs text-gray-600">completed</div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Last Activity</div>
                  <div className="text-xl font-bold text-yellow-600">
                    {getDaysSince(engagement.lastActivityDate) !== null 
                      ? `${getDaysSince(engagement.lastActivityDate)} days ago`
                      : 'Never'}
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900">Pillar Completion Rates</h4>
                {['mind', 'move', 'journal'].map(pillar => {
                  const rate = engagement[`last${timeWindow}Days`]?.[pillar] || 0;
                  return (
                    <div key={pillar} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="capitalize font-medium">{pillar}</span>
                        <span className="font-bold">{rate}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                          className={`h-3 rounded-full ${
                            pillar === 'mind' ? 'bg-blue-500' :
                            pillar === 'move' ? 'bg-green-500' :
                            'bg-purple-500'
                          }`}
                          style={{ width: `${rate}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {pillarBalance && (
                <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">Pillar Balance</h4>
                  <div className="text-sm">
                    {pillarBalance.isBalanced ? (
                      <span className="text-green-600">✓ Pillars are balanced</span>
                    ) : (
                      <span className="text-yellow-600">⚠ Pillar asymmetry detected - consider discussing in session</span>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Section 3: Mood & Craving Trends */}
        {moodCravingTrends && (
          <Card className="mb-6">
            <div
              className="flex justify-between items-center p-6 cursor-pointer border-b"
              onClick={() => toggleSection('moodCraving')}
            >
              <h3 className="text-xl font-bold text-gray-900">Mood & Wellness Trends</h3>
              <svg
                className={`w-6 h-6 transform transition-transform ${expandedSections.moodCraving ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            
            {expandedSections.moodCraving && (
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Last 7 Days</h4>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Avg Mood: </span>
                        <span className="font-bold">{moodCravingTrends.last7Days.averageMood || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Avg Challenge: </span>
                        <span className="font-bold">{moodCravingTrends.last7Days.averageCraving || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Last 30 Days</h4>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Avg Mood: </span>
                        <span className="font-bold">{moodCravingTrends.last30Days.averageMood || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Avg Challenge: </span>
                        <span className="font-bold">{moodCravingTrends.last30Days.averageCraving || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-2">Last 90 Days</h4>
                    <div className="space-y-2">
                      <div>
                        <span className="text-sm text-gray-600">Avg Mood: </span>
                        <span className="font-bold">{moodCravingTrends.last90Days.averageMood || 'N/A'}</span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">Avg Challenge: </span>
                        <span className="font-bold">{moodCravingTrends.last90Days.averageCraving || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {moodCravingTrends.notableShifts && moodCravingTrends.notableShifts.length > 0 && (
                  <div className="p-4 bg-yellow-50 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-2">Notable Shifts</h4>
                    {moodCravingTrends.notableShifts.map((shift, idx) => (
                      <div key={idx} className="text-sm text-yellow-800">
                        ⚠ {shift.type.charAt(0).toUpperCase() + shift.type.slice(1)} change of {shift.change > 0 ? '+' : ''}{shift.change.toFixed(1)} points detected
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* Section 4: Journal Engagement */}
        <Card className="mb-6">
          <div
            className="flex justify-between items-center p-6 cursor-pointer border-b"
            onClick={() => toggleSection('journal')}
          >
            <h3 className="text-xl font-bold text-gray-900">Journal Engagement Signals</h3>
            <svg
              className={`w-6 h-6 transform transition-transform ${expandedSections.journal ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.journal && (
            <div className="p-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                <p className="text-sm text-blue-800">
                  <strong>Privacy Note:</strong> Journal content is encrypted and never exposed to clinicians. 
                  This section shows engagement patterns only.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <div className="text-sm text-gray-600">Completion Rate</div>
                  <div className="text-2xl font-bold text-indigo-600">
                    {journalEngagement.completionRate}%
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600">Total Entries</div>
                  <div className="text-2xl font-bold text-indigo-600">
                    {journalEngagement.totalEntries}
                  </div>
                </div>
                
                <div>
                  <div className="text-sm text-gray-600">Days Since Last Entry</div>
                  <div className="text-2xl font-bold text-indigo-600">
                    {journalEngagement.daysSinceLastEntry !== null ? journalEngagement.daysSinceLastEntry : 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Section 5: Homework & Assignments */}
        <Card className="mb-6">
          <div
            className="flex justify-between items-center p-6 cursor-pointer border-b"
            onClick={() => toggleSection('homework')}
          >
            <h3 className="text-xl font-bold text-gray-900">Homework & Assignments</h3>
            <svg
              className={`w-6 h-6 transform transition-transform ${expandedSections.homework ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.homework && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Total Assigned</div>
                  <div className="text-2xl font-bold text-blue-600">{homework.total}</div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Active</div>
                  <div className="text-2xl font-bold text-green-600">{homework.active}</div>
                </div>
                
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Completed</div>
                  <div className="text-2xl font-bold text-purple-600">{homework.completed}</div>
                </div>
                
                <div className="bg-yellow-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Completion Rate</div>
                  <div className="text-2xl font-bold text-yellow-600">{homework.completionRate}%</div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-4">
                <button
                  onClick={() => setHomeworkFilter('all')}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${
                    homeworkFilter === 'all'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  All ({homework.total})
                </button>
                <button
                  onClick={() => setHomeworkFilter('active')}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${
                    homeworkFilter === 'active'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  Active ({homework.active})
                </button>
                <button
                  onClick={() => setHomeworkFilter('completed')}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${
                    homeworkFilter === 'completed'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  Completed ({homework.completed})
                </button>
                <button
                  onClick={() => setHomeworkFilter('archived')}
                  className={`px-3 py-1.5 text-sm rounded-lg border ${
                    homeworkFilter === 'archived'
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  Archived ({homework.archived || 0})
                </button>
              </div>

              {homework.assignments && homework.assignments.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Assignment Timeline</h4>
                  {filteredHomeworkAssignments.length === 0 && (
                    <p className="text-sm text-gray-500">No assignments in this filter.</p>
                  )}
                  {filteredHomeworkAssignments.map((assignment) => (
                    <div key={assignment._id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <div className="font-medium">{assignment.title}</div>
                          {(assignment.type || assignment.topic) && (
                            <div className="text-xs uppercase tracking-wide text-gray-500 mt-1">{assignment.type || assignment.topic}</div>
                          )}
                          {assignment.sourceLabel && (
                            <div className="text-xs text-indigo-600 mt-1">{assignment.sourceLabel}</div>
                          )}
                          <div className="text-sm text-gray-600">
                            Assigned: {formatDate(assignment.assignedAt)}
                          </div>
                          {assignment.completedAt && (
                            <div className="text-sm text-gray-600">
                              Completed: {formatDate(assignment.completedAt)}
                            </div>
                          )}
                          {assignment.dueDate && (
                            <div className="text-sm text-gray-600">
                              Due: {formatDate(assignment.dueDate)}
                            </div>
                          )}
                        </div>
                        <Badge className={
                          assignment.completionStatus === 'completed' ? 'bg-green-100 text-green-800' :
                          assignment.completionStatus === 'overdue' ? 'bg-red-100 text-red-800' :
                          assignment.completionStatus === 'archived' ? 'bg-gray-100 text-gray-800' :
                          'bg-blue-100 text-blue-800'
                        }>
                          {assignment.completionStatus || (assignment.isActive ? 'active' : 'archived')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Section 6: Encouragement & Communication History */}
        <Card className="mb-6">
          <div
            className="flex justify-between items-center p-6 cursor-pointer border-b"
            onClick={() => toggleSection('encouragement')}
          >
            <h3 className="text-xl font-bold text-gray-900">Communication History</h3>
            <svg
              className={`w-6 h-6 transform transition-transform ${expandedSections.encouragement ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.encouragement && (
            <div className="p-6">
              <div className="mb-4">
                <div className="text-sm text-gray-600">Total Messages Sent</div>
                <div className="text-2xl font-bold text-indigo-600">
                  {encouragementHistory.total}
                </div>
              </div>

              {encouragementHistory.recent && encouragementHistory.recent.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-semibold text-gray-900">Recent Messages</h4>
                  {encouragementHistory.recent.map((message) => (
                    <div key={message._id} className="p-4 bg-gray-50 rounded-lg">
                      <div className="text-sm text-gray-600 mb-2">
                        {formatDateTime(message.sentAt)}
                      </div>
                      <div className="text-sm">
                        "{message.messageText}"
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Section 7: Consent & Access Status */}
        <Card className="mb-6">
          <div
            className="flex justify-between items-center p-6 cursor-pointer border-b"
            onClick={() => toggleSection('consent')}
          >
            <h3 className="text-xl font-bold text-gray-900">Consent & Access Status</h3>
            <svg
              className={`w-6 h-6 transform transition-transform ${expandedSections.consent ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          
          {expandedSections.consent && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Consent Status</h4>
                  <div className="space-y-2">
                    <div>
                      <span className="text-sm text-gray-600">Status: </span>
                      <Badge className={consentStatus.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                        {consentStatus.isActive ? 'Active' : 'Revoked'}
                      </Badge>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Grant Date: </span>
                      <span className="font-medium">{formatDate(consentStatus.grantDate)}</span>
                    </div>
                    <div>
                      <span className="text-sm text-gray-600">Last Review: </span>
                      <span className="font-medium">{formatDate(consentStatus.lastReviewDate)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-900 mb-3">Data Access Scope</h4>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${consentStatus.dataAccessScope.shareEngagement ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className="text-sm">Engagement Metrics</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${consentStatus.dataAccessScope.shareMoodCraving ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className="text-sm">Mood & Wellness Data</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${consentStatus.dataAccessScope.shareJournalEntries ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className="text-sm">Journal Entries (Content)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${consentStatus.dataAccessScope.shareFaithEntries ? 'bg-green-500' : 'bg-gray-300'}`} />
                      <span className="text-sm">Faith Entries</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>HIPAA Compliance:</strong> All access to this profile is logged and auditable. 
                  You can view your complete access history for this client in the audit log.
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Milestones Timeline */}
        {milestones && milestones.length > 0 && (
          <Card className="mb-6">
            <div className="p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Milestone Timeline</h3>
              <div className="space-y-3">
                {milestones.map((milestone, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className={`w-2 h-2 mt-2 rounded-full ${
                      milestone.type === 'phase_transition' ? 'bg-purple-500' :
                      milestone.type === 'streak_milestone' ? 'bg-green-500' :
                      'bg-blue-500'
                    }`} />
                    <div className="flex-1">
                      <div className="font-medium">{milestone.description}</div>
                      {milestone.date && (
                        <div className="text-sm text-gray-600">{formatDate(milestone.date)}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Card>
        )}
      </main>
      <SOSButton />
    </div>
  );
};

export default ClientProfileView;
